import type { Attachment, Document } from "@repo/types";
import {
  BaseExportConverters,
  BaseTableExportService,
  type FieldConverter,
} from "./baseTableExportService";
import { tenantStore } from "@/stores/tenantStore";
import { configRepo } from "@/repositories/configRepo";
import { firstValueFrom } from "rxjs";
import { documentRepo } from "@/repositories/documentRepo";

const baseExportConverters = new BaseExportConverters();

export class DocumentExportService extends BaseTableExportService<Document> {
  constructor() {
    super({
      id: {
        alias: "id",
        columns: {
          value: "id",
        },
        fieldConverter: baseExportConverters.toStringConverter,
        resultFieldname: "ID",
      },
      name: {
        alias: "name",
        columns: {
          value: "name",
        },
        fieldConverter: baseExportConverters.toStringConverter,
        resultFieldname: "Name",
      },
      tags: {
        alias: "tags",
        columns: {
          value: "tags",
        },
        fieldConverter: baseExportConverters.tagsConverter,
        resultFieldname: "Tags",
      },
      status: {
        alias: "status",
        columns: {
          value: "status",
        },
        fieldConverter: baseExportConverters.toStringConverter,
        resultFieldname: "Status",
      },
      description: {
        alias: "description",
        columns: {
          value: "description",
        },
        fieldConverter: baseExportConverters.toStringConverter,
        resultFieldname: "Description",
      },
      confidential: {
        alias: "confidential",
        columns: {
          value: "confidential",
        },
        fieldConverter: baseExportConverters.booleanToYesNoConverter,
        resultFieldname: "Confidential",
      },
      createdAt: {
        alias: "createdAt",
        columns: {
          value: "createdAt",
        },
        fieldConverter: baseExportConverters.toStringConverter,
        resultFieldname: "Created",
      },
      createdBy: {
        alias: "createdBy",
        columns: {
          value: "createdBy",
        },
        fieldConverter: baseExportConverters.userConverter,
        resultFieldname: "Created By",
      },
      updatedAt: {
        alias: "updatedAt",
        columns: {
          value: "updatedAt",
        },
        fieldConverter: baseExportConverters.toStringConverter,
        resultFieldname: "Last Modified",
      },
      updatedBy: {
        alias: "updatedBy",
        columns: {
          value: "updatedBy",
        },
        fieldConverter: baseExportConverters.userConverter,
        resultFieldname: "Modified By",
      },
      teamId: {
        alias: "teamId",
        columns: {
          value: "teamId",
        },
        fieldConverter: baseExportConverters.teamConverter,
        resultFieldname: "Team",
      },
      usedBy: {
        alias: "usedBy",
        columns: {
          value: "parentIds",
        },
        fieldConverter: agendaItemsUsedInConverter,
        resultFieldname: "Agenda Items Used In",
      },
      attachmentName: {
        alias: "attachmentName",
        columns: {
          value: "attachments",
          linked: "linkedAttachment",
        },
        fieldConverter: documentAttachmentNameConverter,
        resultFieldname: "Attachment Name",
      },
      uploadType: {
        alias: "uploadType",
        columns: {
          value: "uploadType",
        },
        fieldConverter: documentUploadTypeConverter,
        resultFieldname: "Document Type",
      },
      ordinanceNumber: {
        alias: "ordinanceNumber",
        columns: {
          value: "ordinanceData.number",
        },
        fieldConverter: baseExportConverters.toStringConverter,
        resultFieldname: "Ordinance Number",
      },
      ordinanceCategory: {
        alias: "ordinanceCategory",
        columns: {
          value: "ordinanceData.category",
        },
        fieldConverter: baseExportConverters.toStringConverter,
        resultFieldname: "Ordinance Category",
      },
      ordinanceStatus: {
        alias: "ordinanceStatus",
        columns: {
          value: "ordinanceData.status",
        },
        fieldConverter: baseExportConverters.toStringConverter,
        resultFieldname: "Ordinance Status",
      },
      url: {
        alias: "",
        columns: {
          value: "attachments",
          linked: "linkedAttachment",
          confidential: "confidential",
        },
        fieldConverter: documentURLConverter,
        resultFieldname: "Document URL",
      },
    });
  }
}

//#region Converters

const agendaItemsUsedInConverter: FieldConverter = async (values, context) => {
  const agendaItemIds = values.value as string[] | undefined;
  if (!agendaItemIds?.length) return "";

  const tenantId = tenantStore.getState().id;
  const items = await Promise.all(
    agendaItemIds.map((id) => context.getAgendaItem(tenantId, id))
  );

  return items
    .filter((item) => !!item)
    .map((item) => item?.name || "Unknown")
    .join("\n");
};
const documentUploadTypeConverter: FieldConverter = async (values) => {
  const { uploadTypes } = await firstValueFrom(
    configRepo.get("documentUploadTypes")
  );
  const uploadType = values.value as string;
  return (
    uploadTypes.find((type) => type.id === uploadType)?.label ?? uploadType
  );
};
const documentAttachmentNameConverter: FieldConverter = async (values) => {
  const attachments = (values.value as Attachment[] | undefined) ?? [];
  const linkedAttachment = values.linked as string | undefined;

  return (
    documentRepo.getLinkedAttachment({ linkedAttachment, attachments })?.name ??
    ""
  );
};
const documentURLConverter: FieldConverter = async (values) => {
  const attachments = (values.value as Attachment[] | undefined) ?? [];
  const linkedAttachment = values.linked as string | undefined;
  const confidential = !!(values.confidential as boolean | undefined);

  const attachment = documentRepo.getLinkedAttachment({
    linkedAttachment,
    attachments,
  });

  if (confidential && attachment?.url) return "[REDACTED]";
  return attachment?.url ?? "";
};

//#endregion

export const documentsExportService = new DocumentExportService();
